import React, { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

/**
 * App.js — SEC Signals Viewer + Summarizer + Exporter
 * - Auto-loads /public/default_signals.csv on startup (if present)
 * - Upload signals CSV (from Colab)
 * - Explore via filters/search
 * - Summarize + basic “what changed” insights
 * - Export filtered signals + taxonomy CSV
 *
 * Dependencies:
 *   npm i papaparse recharts
 */

const BRAND = {
  ink: "#0B1A3C",
  inkMuted: "#64748b",
  bg: "#F7F8FB",
  card: "#FFFFFF",
  line: "#E6EDF2",
  green: "#74C043",
  blue: "#2A73CC",
};

function safeStr(v) {
  return (v ?? "").toString();
}

function parseISODate(s) {
  const t = safeStr(s).trim();
  if (!t) return null;
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d) {
  if (!d) return "";
  return d.toISOString().slice(0, 10);
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCSV(rows, columns) {
  return Papa.unparse(
    rows.map((r) => {
      const out = {};
      columns.forEach((c) => (out[c] = r[c] ?? ""));
      return out;
    }),
    { quotes: true }
  );
}

function uniq(arr) {
  return Array.from(new Set(arr));
}

function groupCount(rows, keyFn) {
  const m = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    m.set(k, (m.get(k) || 0) + 1);
  }
  return m;
}

function topNFromCountMap(map, n = 10) {
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

function buildTaxonomy(rows) {
  const m = new Map();
  for (const r of rows) {
    const form = safeStr(r.form).trim();
    const signal = safeStr(r.signal).trim();
    const k = `${form}|||${signal}`;
    m.set(k, (m.get(k) || 0) + 1);
  }
  return Array.from(m.entries())
    .map(([k, match_count]) => {
      const [form, signal] = k.split("|||");
      return { form, signal, match_count };
    })
    .sort((a, b) => {
      if (a.form === b.form) return b.match_count - a.match_count;
      return a.form.localeCompare(b.form);
    });
}

function buildDailySeries(rows) {
  const m = new Map();
  for (const r of rows) {
    const d = safeStr(r.filing_date).slice(0, 10);
    if (!d) continue;
    m.set(d, (m.get(d) || 0) + 1);
  }
  return Array.from(m.entries())
    .map(([date, matches]) => ({ date, matches }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function buildSignalBar(rows, top = 12) {
  const counts = groupCount(rows, (r) => safeStr(r.signal).trim() || "(blank)");
  return topNFromCountMap(counts, top).map((x) => ({
    signal: x.key.length > 28 ? x.key.slice(0, 28) + "…" : x.key,
    matches: x.count,
    _full: x.key,
  }));
}

function buildFormBar(rows) {
  const counts = groupCount(rows, (r) => safeStr(r.form).trim() || "(blank)");
  return topNFromCountMap(counts, 20).map((x) => ({
    form: x.key,
    matches: x.count,
  }));
}

function Card({ title, subtitle, children, right }) {
  return (
    <div
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.line}`,
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 10px 30px -18px rgba(2,6,23,0.22)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: BRAND.ink, fontSize: 14 }}>
            {title}
          </div>
          {subtitle ? (
            <div style={{ color: BRAND.inkMuted, fontSize: 12, marginTop: 4 }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

function Pill({ text, onClick, active }) {
  return (
    <button
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        border: `1px solid ${active ? BRAND.blue : BRAND.line}`,
        background: active ? "rgba(42,115,204,0.10)" : "#fff",
        color: BRAND.ink,
        borderRadius: 999,
        padding: "6px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {text}
    </button>
  );
}

export default function App() {
  const fileRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [filename, setFilename] = useState("");
  const [loadedFrom, setLoadedFrom] = useState(""); // "default" | "upload" | ""

  // filters
  const [q, setQ] = useState("");
  const [formFilter, setFormFilter] = useState("ALL");
  const [signalFilter, setSignalFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // pagination / display
  const [limit, setLimit] = useState(200);

  // ---------- CSV parsing helpers ----------
  function normalizeRows(data) {
    return data.map((r) => ({
      ...r,
      issuer_name: safeStr(r.issuer_name).trim(),
      ticker: safeStr(r.ticker).trim(),
      cik: safeStr(r.cik).trim(),
      form: safeStr(r.form).trim(),
      filing_date: safeStr(r.filing_date).trim(),
      accession_number: safeStr(r.accession_number).trim(),
      filing_url: safeStr(r.filing_url).trim(),
      run_ts_utc: safeStr(r.run_ts_utc).trim(),
      signal: safeStr(r.signal).trim(),
      snippet: safeStr(r.snippet),
      char_start: safeStr(r.char_start).trim(),
      char_end: safeStr(r.char_end).trim(),
    }));
  }

  function parseCsvText(csvText, sourceName = "csv") {
    setError("");
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (res) => {
        const raw = res.data || [];
        const data = raw.filter((r) =>
          Object.values(r || {}).some((v) => safeStr(v).trim())
        );

        if (!data.length) {
          setRows([]);
          setError(
            `Parsed 0 rows from ${sourceName}. Check that it is the signals CSV and that the first row is the header.`
          );
          return;
        }

        setRows(normalizeRows(data));
      },
      error: (err) =>
        setError(err?.message || `Failed to parse ${sourceName}.`),
    });
  }

  function handleFile(file) {
    if (!file) return;
    setFilename(file.name);
    setLoadedFrom("upload");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (res) => {
        const raw = res.data || [];
        const data = raw.filter((r) =>
          Object.values(r || {}).some((v) => safeStr(v).trim())
        );

        if (!data.length) {
          setRows([]);
          setError("Parsed 0 rows. Is this the signals CSV (not taxonomy)?");
          return;
        }
        setRows(normalizeRows(data));
      },
      error: (err) => setError(err?.message || "Failed to parse CSV."),
    });
  }

  // ---------- AUTO-LOAD DEFAULT CSV ----------
  useEffect(() => {
    // Only attempt default load if nothing is loaded yet
    if (rows.length) return;

    const DEFAULT_PATH = "/default_signals.csv";
    // cache-bust so CodeSandbox refresh always pulls latest file contents
    const url = `${DEFAULT_PATH}?v=${Date.now()}`;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error("default file not found");
        return r.text();
      })
      .then((text) => {
        // If the file is empty or only headers, show a helpful error
        setFilename("default_signals.csv");
        setLoadedFrom("default");
        parseCsvText(text, "default_signals.csv");
      })
      .catch(() => {
        // silent: user can upload manually
      });
  }, []);

  // ---------- Derived UI state ----------
  const columns = useMemo(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const forms = useMemo(() => {
    return [
      "ALL",
      ...uniq(rows.map((r) => safeStr(r.form).trim()).filter(Boolean)),
    ];
  }, [rows]);

  const signals = useMemo(() => {
    return [
      "ALL",
      ...uniq(rows.map((r) => safeStr(r.signal).trim()).filter(Boolean)),
    ];
  }, [rows]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const sd = startDate ? parseISODate(startDate) : null;
    const ed = endDate ? parseISODate(endDate) : null;

    return rows.filter((r) => {
      if (formFilter !== "ALL" && safeStr(r.form).trim() !== formFilter)
        return false;
      if (signalFilter !== "ALL" && safeStr(r.signal).trim() !== signalFilter)
        return false;

      if (sd || ed) {
        const d = parseISODate(r.filing_date);
        if (!d) return false;
        if (sd && d < sd) return false;
        if (ed && d > ed) return false;
      }

      if (qq) {
        const hay = [
          r.issuer_name,
          r.ticker,
          r.form,
          r.filing_date,
          r.signal,
          r.snippet,
          r.filing_url,
          r.accession_number,
        ]
          .map(safeStr)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(qq)) return false;
      }

      return true;
    });
  }, [rows, q, formFilter, signalFilter, startDate, endDate]);

  const filteredLimited = useMemo(
    () => filtered.slice(0, limit),
    [filtered, limit]
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const shown = filtered.length;

    const uniqueSignals = uniq(
      filtered.map((r) => safeStr(r.signal).trim()).filter(Boolean)
    ).length;
    const uniqueForms = uniq(
      filtered.map((r) => safeStr(r.form).trim()).filter(Boolean)
    ).length;

    const dates = filtered
      .map((r) => parseISODate(r.filing_date))
      .filter(Boolean)
      .sort((a, b) => b - a);
    const latest = dates[0] ? formatDate(dates[0]) : "";

    return { total, shown, uniqueSignals, uniqueForms, latest };
  }, [rows, filtered]);

  const dailySeries = useMemo(() => buildDailySeries(filtered), [filtered]);
  const signalBar = useMemo(() => buildSignalBar(filtered, 12), [filtered]);
  const formBar = useMemo(() => buildFormBar(filtered), [filtered]);

  const latestInsights = useMemo(() => {
    const byDate = new Map();
    for (const r of filtered) {
      const d = safeStr(r.filing_date).slice(0, 10);
      if (!d) continue;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(r);
    }
    const dates = Array.from(byDate.keys()).sort((a, b) => b.localeCompare(a));
    const latest = dates[0];
    const prev = dates[1];

    if (!latest)
      return { latest: "", prev: "", newSignals: [], topSignals: [] };

    const latestRows = byDate.get(latest) || [];
    const prevRows = prev ? byDate.get(prev) || [] : [];

    const latestSet = new Set(latestRows.map((r) => safeStr(r.signal).trim()));
    const prevSet = new Set(prevRows.map((r) => safeStr(r.signal).trim()));

    const newSignals = Array.from(latestSet).filter(
      (s) => s && !prevSet.has(s)
    );
    const topSignals = topNFromCountMap(
      groupCount(latestRows, (r) => safeStr(r.signal).trim()),
      6
    );

    return { latest, prev: prev || "", newSignals, topSignals };
  }, [filtered]);

  function reset() {
    setRows([]);
    setFilename("");
    setLoadedFrom("");
    setError("");
    setQ("");
    setFormFilter("ALL");
    setSignalFilter("ALL");
    setStartDate("");
    setEndDate("");
    setLimit(200);
  }

  function onDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function exportFilteredSignals() {
    if (!filtered.length) return;
    const cols = columns.length ? columns : Object.keys(filtered[0] || {});
    const csv = toCSV(filtered, cols);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "_");
    downloadText(`signals_filtered_${ts}.csv`, csv);
  }

  function exportTaxonomy() {
    const tax = buildTaxonomy(filtered);
    const csv = Papa.unparse(tax, { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "_");
    downloadText(`taxonomy_filtered_${ts}.csv`, csv);
  }

  const executiveSummary = useMemo(() => {
    if (!filtered.length) return "";
    const topSignals = topNFromCountMap(
      groupCount(filtered, (r) => safeStr(r.signal).trim()),
      8
    );
    const topForms = topNFromCountMap(
      groupCount(filtered, (r) => safeStr(r.form).trim()),
      6
    );

    const issuer = safeStr(filtered[0].issuer_name) || "Issuer";
    const ticker = safeStr(filtered[0].ticker) || "";
    const latest = stats.latest || latestInsights.latest || "";

    const bullets = [];
    bullets.push(
      `Dataset: ${issuer}${
        ticker ? ` (${ticker})` : ""
      } — ${filtered.length.toLocaleString()} signal matches in current view.`
    );
    if (latest) bullets.push(`Latest filing date in view: ${latest}.`);
    bullets.push(
      `Most frequent signals: ${topSignals
        .slice(0, 5)
        .map((x) => `${x.key} (${x.count})`)
        .join(", ")}.`
    );
    bullets.push(
      `Most frequent forms: ${topForms
        .slice(0, 4)
        .map((x) => `${x.key} (${x.count})`)
        .join(", ")}.`
    );

    if (latestInsights.latest && latestInsights.newSignals?.length) {
      bullets.push(
        `New vs prior filing-date bucket (${latestInsights.prev || "n/a"} → ${
          latestInsights.latest
        }): ${latestInsights.newSignals.slice(0, 8).join(", ")}${
          latestInsights.newSignals.length > 8 ? "…" : ""
        }`
      );
    }

    const top1 = topSignals[0]?.key;
    if (top1) {
      const sample = filtered.find((r) => safeStr(r.signal).trim() === top1);
      if (sample?.snippet) {
        bullets.push(
          `Example snippet (${top1}): “${safeStr(sample.snippet).slice(
            0,
            220
          )}${safeStr(sample.snippet).length > 220 ? "…" : ""}”`
        );
      }
    }

    return bullets.map((b) => `• ${b}`).join("\n");
  }, [filtered, stats.latest, latestInsights]);

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, color: BRAND.ink }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 20 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.3 }}>
              SEC Filing Signals Viewer
            </div>
            <div style={{ marginTop: 6, color: BRAND.inkMuted, fontSize: 13 }}>
              Auto-loads <b>/public/default_signals.csv</b> (if present) — or
              upload <b>signals_*.csv</b>.
            </div>
            {filename ? (
              <div
                style={{ marginTop: 6, color: BRAND.inkMuted, fontSize: 12 }}
              >
                Loaded: <b>{filename}</b>
                {loadedFrom ? (
                  <>
                    {" "}
                    <span style={{ color: BRAND.inkMuted }}>
                      •
                    </span> source: <b>{loadedFrom}</b>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                border: `1px solid ${BRAND.line}`,
                background: "#fff",
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Upload CSV
            </button>

            <button
              onClick={exportFilteredSignals}
              disabled={!filtered.length}
              style={{
                border: `1px solid ${
                  filtered.length ? BRAND.blue : BRAND.line
                }`,
                background: filtered.length ? BRAND.blue : "#fff",
                color: filtered.length ? "#fff" : BRAND.inkMuted,
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                cursor: filtered.length ? "pointer" : "not-allowed",
              }}
            >
              Export Filtered Signals
            </button>

            <button
              onClick={exportTaxonomy}
              disabled={!filtered.length}
              style={{
                border: `1px solid ${
                  filtered.length ? BRAND.green : BRAND.line
                }`,
                background: filtered.length ? BRAND.green : "#fff",
                color: filtered.length ? "#0b1a3c" : BRAND.inkMuted,
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                cursor: filtered.length ? "pointer" : "not-allowed",
              }}
            >
              Export Taxonomy
            </button>

            <button
              onClick={reset}
              style={{
                border: `1px solid ${BRAND.line}`,
                background: "transparent",
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 800,
                cursor: "pointer",
                color: BRAND.inkMuted,
              }}
            >
              Reset
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
        </div>

        {/* Dropzone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            marginTop: 16,
            border: `1.5px dashed ${BRAND.line}`,
            borderRadius: 16,
            padding: 16,
            background: "rgba(255,255,255,0.55)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontWeight: 900 }}>
                Drag & drop your signals CSV here
              </div>
              <div
                style={{ color: BRAND.inkMuted, fontSize: 12, marginTop: 4 }}
              >
                If your default file isn’t loading, confirm it’s at{" "}
                <b>public/default_signals.csv</b> and refresh.
              </div>
            </div>
            {error ? (
              <div style={{ color: "#C0362C", fontWeight: 900, fontSize: 12 }}>
                {error}
              </div>
            ) : null}
          </div>
        </div>

        {/* Stats */}
        <div
          style={{
            marginTop: 16,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 12,
          }}
        >
          <Card title="Rows (total)" subtitle="All rows loaded from CSV">
            <div style={{ fontSize: 22, fontWeight: 1000 }}>
              {stats.total.toLocaleString()}
            </div>
          </Card>
          <Card title="Rows (in view)" subtitle="After filters/search">
            <div style={{ fontSize: 22, fontWeight: 1000 }}>
              {stats.shown.toLocaleString()}
            </div>
          </Card>
          <Card
            title="Unique signals"
            subtitle="Signals present in current view"
          >
            <div style={{ fontSize: 22, fontWeight: 1000 }}>
              {stats.uniqueSignals.toLocaleString()}
            </div>
          </Card>
          <Card
            title="Latest filing date"
            subtitle="Max filing_date in current view"
          >
            <div style={{ fontSize: 18, fontWeight: 1000 }}>
              {stats.latest || "—"}
            </div>
          </Card>
        </div>

        {/* Controls */}
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
            gap: 10,
          }}
        >
          <Card
            title="Search"
            subtitle="Looks across issuer/ticker/form/signal/snippet/url"
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type keywords like 'inventory', 'share repurchase', 'litigation'…"
              style={{
                width: "100%",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 12,
                padding: 10,
                outline: "none",
                fontWeight: 700,
              }}
            />
          </Card>

          <Card title="Form filter" subtitle="10-K / 10-Q / 8-K / DEF 14A…">
            <select
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 12,
                padding: 10,
                outline: "none",
                fontWeight: 800,
                background: "#fff",
              }}
            >
              {forms.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Card>

          <Card title="Signal filter" subtitle="Choose one phrase">
            <select
              value={signalFilter}
              onChange={(e) => setSignalFilter(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 12,
                padding: 10,
                outline: "none",
                fontWeight: 800,
                background: "#fff",
              }}
            >
              {signals.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Card>

          <Card title="Start date" subtitle="YYYY-MM-DD">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 12,
                padding: 10,
                outline: "none",
                fontWeight: 800,
              }}
            />
          </Card>

          <Card title="End date" subtitle="YYYY-MM-DD">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 12,
                padding: 10,
                outline: "none",
                fontWeight: 800,
              }}
            />
          </Card>
        </div>

        {/* Summaries */}
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr",
            gap: 12,
          }}
        >
          <Card
            title="Executive summary"
            subtitle="Auto-generated from the current view (filters apply)"
            right={
              <button
                onClick={() =>
                  executiveSummary &&
                  navigator.clipboard?.writeText(executiveSummary)
                }
                style={{
                  border: `1px solid ${BRAND.line}`,
                  background: "#fff",
                  borderRadius: 12,
                  padding: "8px 10px",
                  fontWeight: 900,
                  cursor: executiveSummary ? "pointer" : "not-allowed",
                  color: BRAND.ink,
                }}
              >
                Copy
              </button>
            }
          >
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 12,
                color: BRAND.ink,
                lineHeight: 1.35,
              }}
            >
              {executiveSummary || "Upload a CSV to generate a summary."}
            </pre>

            {latestInsights.latest ? (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Pill text={`Latest: ${latestInsights.latest}`} />
                {latestInsights.prev ? (
                  <Pill text={`Prev: ${latestInsights.prev}`} />
                ) : null}
                <Pill
                  text={`New signals: ${
                    latestInsights.newSignals?.length || 0
                  }`}
                />
              </div>
            ) : null}
          </Card>

          <Card
            title="What’s most frequent right now"
            subtitle="Top signals + top forms (current view)"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 12,
                    color: BRAND.inkMuted,
                    marginBottom: 8,
                  }}
                >
                  Top signals
                </div>
                {topNFromCountMap(
                  groupCount(filtered, (r) => safeStr(r.signal).trim()),
                  8
                ).map((x) => (
                  <div
                    key={x.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 6,
                    }}
                  >
                    <button
                      onClick={() => setSignalFilter(x.key)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        color: BRAND.blue,
                        fontWeight: 900,
                        fontSize: 12,
                      }}
                      title="Click to filter to this signal"
                    >
                      {x.key}
                    </button>
                    <div style={{ fontWeight: 1000, fontSize: 12 }}>
                      {x.count.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 12,
                    color: BRAND.inkMuted,
                    marginBottom: 8,
                  }}
                >
                  Top forms
                </div>
                {topNFromCountMap(
                  groupCount(filtered, (r) => safeStr(r.form).trim()),
                  8
                ).map((x) => (
                  <div
                    key={x.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 6,
                    }}
                  >
                    <button
                      onClick={() => setFormFilter(x.key)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        color: BRAND.blue,
                        fontWeight: 900,
                        fontSize: 12,
                      }}
                      title="Click to filter to this form"
                    >
                      {x.key}
                    </button>
                    <div style={{ fontWeight: 1000, fontSize: 12 }}>
                      {x.count.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <Card
            title="Matches over time"
            subtitle="Count of signal hits by filing_date (current view)"
          >
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={dailySeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="matches"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            title="Top signals"
            subtitle="Most frequent signals (current view)"
          >
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={signalBar}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="signal"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-18}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="matches" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Table */}
        <div style={{ marginTop: 12 }}>
          <Card
            title="Signal matches"
            subtitle={`Showing ${Math.min(
              filteredLimited.length,
              filtered.length
            ).toLocaleString()} of ${filtered.length.toLocaleString()} rows (current view).`}
            right={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div
                  style={{
                    fontSize: 12,
                    color: BRAND.inkMuted,
                    fontWeight: 800,
                  }}
                >
                  Rows:
                </div>
                {[200, 500, 1000, 2000].map((n) => (
                  <Pill
                    key={n}
                    text={`${n}`}
                    active={limit === n}
                    onClick={() => setLimit(n)}
                  />
                ))}
              </div>
            }
          >
            <div
              style={{
                overflowX: "auto",
                border: `1px solid ${BRAND.line}`,
                borderRadius: 14,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    {[
                      "filing_date",
                      "form",
                      "signal",
                      "snippet",
                      "filing_url",
                      "accession_number",
                    ].map((c) => (
                      <th
                        key={c}
                        style={{
                          textAlign: "left",
                          fontSize: 12,
                          padding: 10,
                          borderBottom: `1px solid ${BRAND.line}`,
                          color: BRAND.inkMuted,
                          fontWeight: 1000,
                          background: "rgba(230,237,242,0.35)",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLimited.map((r, idx) => (
                    <tr
                      key={idx}
                      style={{ borderBottom: `1px solid ${BRAND.line}` }}
                    >
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {safeStr(r.filing_date).slice(0, 10)}
                      </td>
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {safeStr(r.form)}
                      </td>
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button
                          onClick={() =>
                            setSignalFilter(safeStr(r.signal).trim() || "ALL")
                          }
                          style={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            color: BRAND.blue,
                            fontWeight: 1000,
                          }}
                          title="Click to filter to this signal"
                        >
                          {safeStr(r.signal)}
                        </button>
                      </td>
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          color: BRAND.ink,
                          minWidth: 420,
                          lineHeight: 1.35,
                        }}
                      >
                        {safeStr(r.snippet)}
                      </td>
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.filing_url ? (
                          <a
                            href={safeStr(r.filing_url)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: BRAND.blue, fontWeight: 900 }}
                          >
                            open
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td
                        style={{
                          padding: 10,
                          fontSize: 12,
                          color: BRAND.inkMuted,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {safeStr(r.accession_number) || "—"}
                      </td>
                    </tr>
                  ))}
                  {!filteredLimited.length ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: 14,
                          color: BRAND.inkMuted,
                          fontWeight: 800,
                        }}
                      >
                        No rows match your current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 18, color: BRAND.inkMuted, fontSize: 12 }}>
          Tip: If the default CSV still doesn’t load, open{" "}
          <b>/default_signals.csv</b> in a new browser tab inside the sandbox.
          If it 404s, it’s not being served from <b>public/</b>.
        </div>
      </div>
    </div>
  );
}
